import {
  type BucketBalances,
  type GameHold,
  type GameHoldOutcomeFilter,
  type LedgerReportName,
  type LedgerReportRange,
  PlayerHistoryCut,
} from "@pax/api-contract/accounting/v1alpha1";
import type { GameInfo, ListMembershipsResponse } from "@pax/api-contract/games/v1alpha1";
import {
  infiniteQueryOptions,
  keepPreviousData,
  type QueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { api, isNotFound, orpc, reachable } from "@/lib/api";
import { type BalanceChange, balanceChange } from "./balances";
import { instantFrom } from "./format";

/** Keeps the same report's last range on screen while the next one loads. */
export const ledgerReportQuery = (report: LedgerReportName, range: LedgerReportRange, back = 0) =>
  orpc.accounting.reports.get.queryOptions({
    input: { params: { report }, query: { range, ...(back > 0 ? { back } : {}) } },
    placeholderData: previous => (previous?.report === report ? previous : undefined),
  });

/** Accounts with this exact user id, or whose email or username starts with this text. */
export const playerSearchQuery = (q: string) =>
  orpc.profiles.accounts.search.queryOptions({
    input: { query: { q, limit: 8 } },
    select: response => response.accounts,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const playerBalanceQuery = (userId: string) =>
  queryOptions({
    queryKey: orpc.accounting.players.balances.get.queryKey({
      input: { params: { userId }, headers: {} },
    }),
    queryFn: async () => {
      const reply = await reachable(
        api.accounting.players.balances.get({ params: { userId }, headers: {} }),
      );
      if (reply.status !== 200) throw new Error("A balance read without an ETag answered 304.");
      return reply.body;
    },
  });

/** Holds change by the second, so this rereads on focus once it is a few seconds old. */
export const playerOpenHoldsQuery = (userId: string) =>
  orpc.accounting.players.openHolds.list.queryOptions({
    input: { params: { userId } },
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });

/** Where a page of a player's history starts: older than the cut `before`, and no later than `at`. */
interface PlayerHistoryStart {
  at?: string;
  before?: string;
}

type PlayerHistoryPage = PlayerHistoryStart & { limit: number };

/**
 * Where a player's history starts, as the URL carries it: an instant, a cut, or a cut and the
 * instant the pages were jumped to, as `<cut>@<instant>`. Undefined for anything else.
 */
export function historyStart(value: unknown): PlayerHistoryStart | undefined {
  if (typeof value !== "string") return undefined;
  const [cut, instant, ...rest] = value.split("@");
  if (rest.length > 0) return undefined;
  if (instant !== undefined) {
    const at = instantFrom(instant);
    return at && PlayerHistoryCut.safeParse(cut).success ? { at, before: cut } : undefined;
  }
  if (PlayerHistoryCut.safeParse(value).success) return { before: value };
  const at = instantFrom(value);
  return at ? { at } : undefined;
}

/** A history start as the URL carries it, which `historyStart` reads back. */
export const historyStartValue = ({ at, before }: PlayerHistoryStart) =>
  before && at ? `${before}@${at}` : (before ?? at);

const playerHistoryInput = (userId: string, page: PlayerHistoryPage) => ({
  params: { userId },
  query: {
    limit: page.limit,
    ...(page.at ? { at: page.at } : {}),
    ...(page.before ? { before: page.before } : {}),
  },
});

/** One page of a player's operations and released holds, newest first. */
export const playerHistoryQuery = (userId: string, page: PlayerHistoryPage) =>
  orpc.accounting.players.operations.list.queryOptions({
    input: playerHistoryInput(userId, page),
    placeholderData: keepPreviousData,
  });

const DAY_MS = 24 * 60 * 60 * 1000;
const BALANCE_READ_LIMIT = 500;

/** One UTC day of a player's balance changes, newest first, from `from` up to the day after. */
export interface PlayerBalanceDay {
  from: number;
  changes: BalanceChange[];
  /** The day holds the start of the player's history, and every change before the day too. */
  reachedStart: boolean;
  /** On today's page, the balance the page had read when today was read. */
  balance?: BucketBalances;
}

/**
 * A player's balance changes a UTC day at a time, back from now, each day read a history page at a
 * time. Page `k` is the day `k` days before `today`, so today's runs up to now and carries the
 * balance to work back from.
 */
export const playerBalanceDaysQuery = (userId: string, today: string) =>
  infiniteQueryOptions({
    queryKey: ["ledger", "playerBalanceDays", userId, today],
    initialPageParam: 0,
    queryFn: async ({ client, pageParam, signal }): Promise<PlayerBalanceDay> => {
      const balance =
        pageParam === 0
          ? (await client.ensureQueryData(playerBalanceQuery(userId))).buckets
          : undefined;
      const from = Date.parse(today) - pageParam * DAY_MS;
      const at = pageParam === 0 ? undefined : new Date(from + DAY_MS).toISOString();
      const changes: BalanceChange[] = [];
      let before: string | undefined;
      for (;;) {
        const page = await reachable(
          api.accounting.players.operations.list(
            playerHistoryInput(userId, { at, before, limit: BALANCE_READ_LIMIT }),
            { signal },
          ),
        );
        for (const item of page.items) {
          if (
            item.kind === "operation" &&
            (!page.nextBefore || Date.parse(item.recordedAt) >= from)
          ) {
            changes.push(balanceChange(item));
          }
        }
        const oldest = page.items.at(-1);
        if (!page.nextBefore) return { from, changes, reachedStart: true, balance };
        if (!oldest) return { from, changes, reachedStart: false, balance };
        const oldestAt = oldest.kind === "operation" ? oldest.recordedAt : oldest.releasedAt;
        if (Date.parse(oldestAt) < from) return { from, changes, reachedStart: false, balance };
        before = page.nextBefore;
      }
    },
    getNextPageParam: (last, pages) => (last.reachedStart ? undefined : pages.length),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

/** A page of the games a player owns or has joined. */
export const playerMembershipsQuery = (userId: string) =>
  orpc.games.memberships.list.infiniteOptions({
    input: (cursor: string | undefined) => ({
      query: { userId, limit: 10, ...(cursor ? { cursor } : {}) },
    }),
    initialPageParam: undefined,
    getNextPageParam: (page: ListMembershipsResponse) => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });

/**
 * Whether this player shared a hold on the game, and the game's title when it still exists. Null
 * when they have no hold on it.
 */
export const playerPaidGameQuery = (userId: string, gameId: string) =>
  queryOptions({
    queryKey: ["ledger", "playerPaidGame", userId, gameId],
    queryFn: async () => {
      const page = await reachable(
        api.accounting.games.holds.list({
          params: { gameId },
          query: { payerUserId: userId, limit: 1 },
        }),
      );
      if (page.holds.length === 0) return null;
      try {
        const { game } = await reachable(api.games.gameInfo.get({ params: { id: gameId } }));
        return { gameId, title: game.title };
      } catch (error) {
        if (isNotFound(error)) return { gameId, title: null };
        throw error;
      }
    },
  });

/** A game's title, preset, and owner. Null once the game is deleted. */
export const gameInfoQuery = (gameId: string) =>
  queryOptions({
    queryKey: orpc.games.gameInfo.get.queryKey({ input: { params: { id: gameId } } }),
    queryFn: async (): Promise<GameInfo | null> => {
      try {
        const { game } = await reachable(api.games.gameInfo.get({ params: { id: gameId } }));
        return game;
      } catch (error) {
        if (isNotFound(error)) return null;
        throw error;
      }
    },
    staleTime: 5_000,
  });

/** Which of a game's holds to read: how they ended, when they were placed, and who paid. */
export interface GameHoldsFilter {
  outcome?: GameHoldOutcomeFilter;
  from?: string;
  to?: string;
  payerUserId?: string;
}

const gameHoldsInput = (
  gameId: string,
  filter: GameHoldsFilter,
  page: { before?: string; limit: number },
) => ({
  params: { gameId },
  query: {
    limit: page.limit,
    ...(page.before ? { beforeHoldId: page.before } : {}),
    ...(filter.outcome ? { outcome: filter.outcome } : {}),
    ...(filter.from ? { from: filter.from } : {}),
    ...(filter.to ? { to: filter.to } : {}),
    ...(filter.payerUserId ? { payerUserId: filter.payerUserId } : {}),
  },
});

/** One page of a game's holds, newest placed first, older than `before`. */
export const gameHoldsQuery = (
  gameId: string,
  filter: GameHoldsFilter,
  page: { before?: string; limit: number },
) =>
  orpc.accounting.games.holds.list.queryOptions({
    input: gameHoldsInput(gameId, filter, page),
    placeholderData: keepPreviousData,
  });

/** One UTC day of a game's holds, newest placed first, from `from` up to the day after. */
export interface GameHoldsDay {
  from: number;
  holds: GameHold[];
  /** Nothing older than this day matches. */
  reachedStart: boolean;
}

/**
 * A game's holds a UTC day at a time, back from now, each day read a page at a time. Page `k` is
 * the day `k` days before `today`, so today's runs up to now. A probe of one hold placed before
 * the day tells whether anything older exists.
 */
export const gameHoldsDaysQuery = (gameId: string, today: string) =>
  infiniteQueryOptions({
    queryKey: ["ledger", "gameHoldsDays", gameId, today],
    initialPageParam: 0,
    queryFn: async ({ pageParam, signal }): Promise<GameHoldsDay> => {
      const from = Date.parse(today) - pageParam * DAY_MS;
      const filter: GameHoldsFilter = {
        from: new Date(from).toISOString(),
        ...(pageParam === 0 ? {} : { to: new Date(from + DAY_MS).toISOString() }),
      };
      const holds: GameHold[] = [];
      let before: string | undefined;
      do {
        const page = await reachable(
          api.accounting.games.holds.list(
            gameHoldsInput(gameId, filter, { before, limit: BALANCE_READ_LIMIT }),
            { signal },
          ),
        );
        holds.push(...page.holds);
        before = page.nextBeforeHoldId ?? undefined;
      } while (before);
      const older = await reachable(
        api.accounting.games.holds.list(
          { params: { gameId }, query: { to: new Date(from).toISOString(), limit: 1 } },
          { signal },
        ),
      );
      return { from, holds, reachedStart: older.holds.length === 0 };
    },
    getNextPageParam: (last, pages) => (last.reachedStart ? undefined : pages.length),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

/**
 * The cut that reads the page just newer than where this one starts: just newer than `before`, or
 * than `at` with no cut. Undefined when that page reaches the newest.
 */
export async function fetchPlayerHistoryNewerStart(userId: string, page: PlayerHistoryPage) {
  const input = playerHistoryInput(userId, page);
  const body = await reachable(
    api.accounting.players.operations.list({ ...input, query: { ...input.query, newer: "true" } }),
  );
  return body.newer ?? undefined;
}

/**
 * The cursor that reads the page of holds just newer than the ones before `before`, starting with
 * that hold. Undefined when that page reaches the newest.
 */
export async function fetchGameHoldsNewerStart(
  gameId: string,
  filter: GameHoldsFilter,
  page: { before: string; limit: number },
) {
  const input = gameHoldsInput(gameId, filter, page);
  const body = await reachable(
    api.accounting.games.holds.list({ ...input, query: { ...input.query, newer: "true" } }),
  );
  return body.newer ?? undefined;
}

/** One page of support adjustments, newest first, older than `before`, by who made them and for whom. */
export const supportAdjustmentsQuery = (
  filter: { granterUserId?: string; recipientUserId?: string },
  before: string | undefined,
) =>
  orpc.accounting.support.adjustments.list.queryOptions({
    input: {
      query: {
        ...(filter.granterUserId ? { granterUserId: filter.granterUserId } : {}),
        ...(filter.recipientUserId ? { recipientUserId: filter.recipientUserId } : {}),
        ...(before ? { beforeOperationId: before } : {}),
      },
    },
    placeholderData: keepPreviousData,
  });

/** After a grant or removal: the player's balances and history, and the adjustments list. */
export function refreshAfterSupportAdjustment(queryClient: QueryClient, userId: string) {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: orpc.accounting.players.balances.get.key({ input: { params: { userId } } }),
    }),
    queryClient.invalidateQueries({
      queryKey: orpc.accounting.players.operations.list.key({ input: { params: { userId } } }),
    }),
    queryClient.invalidateQueries({ queryKey: orpc.accounting.support.adjustments.list.key() }),
  ]);
}
